import classNames from 'classnames';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	checkContent,
	renameDocument,
} from '../../../store/actions/admin/content-actions';
import { EventsState } from '../../../store/types';
import {
	FILE_NUMBER_MAP,
	FILE_TYPES,
	FILE_TYPE_KEYS,
} from '../../../store/utils/content-utils';
import { Document } from '../../../types/working-model';
import {
	bytesToString,
	extractFileType,
	getImageLocation,
} from '../../../utils/utils';
import Checkbox from '../../general-ui/checkbox/checkbox';
import CustomCheckbox from '../../general-ui/checkbox/custom-checkbox';
import EditableTitle from '../../general-ui/editable-title/editable-title';
import Icon, { COLORS, ICONS } from '../../general-ui/icon';

interface DownloadIconProps {
	title: string;
	className: string;
}
export function DownloadIcon({ title = '', className = '' }: DownloadIconProps): JSX.Element {
	return (
		<div className={classNames('download-icon', className)}>
			<Icon name={ICONS.VIEWERS_EYE} size={20} color={COLORS.WHITE} />
			<span>{title}</span>
		</div>
	);
}

interface DocumentItemProps {
	document: Document;
}

export default function DocumentItem({
	document,
}: DocumentItemProps): JSX.Element {
	const {
		ContentReducer: { checkedSet, fileType, view },
		AuthReducer: { token },
	} = useSelector((state: EventsState) => state);
	const dispatch = useDispatch();

	const [imgError, setImgError] = useState(false);

	const isChecked = checkedSet.has(document.document);

	const handleCheck = () => {
		dispatch(checkContent(document.document));
	};

	const handleSubmitEdit = (newName: string) => {
		dispatch(renameDocument(newName, document.document, token!));
	};

	const handleImgError = () => setImgError(true);
	const imgSrc = getImageLocation(document.thumbnail);

	const file = extractFileType(document.original_name);
	const title = file.toUpperCase();
	const uploadedAt = new Date(document.created_at).toLocaleDateString();
	const fileNumber = FILE_NUMBER_MAP[file] || FILE_TYPES.MISC;
	const category = FILE_TYPE_KEYS[fileNumber];
	const listView = view === 'list';
	const gridView = view === 'grid';
	const notImage = fileType !== FILE_TYPES.IMAGES;

	return (
		<li className={classNames('content-item', `${view}-item`)}>
			{gridView && (
				<>
					<div
						className={classNames('thumbnail', {
							error: imgError || !imgSrc,
						})}
					>
						{!imgError && imgSrc ? (
							<img
								className="thumbnail"
								src={imgSrc}
								onError={handleImgError}
								alt=""
							/>
						) : (
							<Icon name={ICONS.IMAGE_INVERSE} size={64} color="" />
						)}
					</div>
					{notImage && (
						<a
							className="document-download"
							rel="noreferrer"
							target="_blank"
							href={document.location}
						>
							<DownloadIcon title={title} className={category} />
						</a>
					)}
				</>
			)}

			<ListGridCheckbox
				isChecked={isChecked}
				id={document.document}
				gridView={gridView}
				onChange={handleCheck}
			/>

			{(listView || notImage) && (
				<EditableTitle
					defaultText={document.display_name.base}
					onSubmit={handleSubmitEdit}
					className="item-title"
				/>
			)}

			{listView && (
				<>
					<p>{bytesToString(document.filesize)}</p>
					<p>{uploadedAt}</p>
					<hr className="hr" />
				</>
			)}
		</li>
	);
}

interface ListGridCheckboxProps {
	id: number | string;
	gridView: boolean;
	onChange: () => void;
	isChecked: boolean;
}

export function ListGridCheckbox({
	id,
	gridView,
	onChange,
	isChecked,
}: ListGridCheckboxProps): JSX.Element {
	return (
		<div className="checkbox-container">
			{gridView ? (
				<Checkbox
					size={20}
					checked={isChecked}
					value={id}
					onChange={onChange}
					backgroundColor={isChecked ? COLORS.WHITE : '#878793'}
					inactiveColor={COLORS.DEFAULT_GRAY}
					activeColor={COLORS.DEFAULT_GRAY}
				/>
			) : (
				<CustomCheckbox
					size={16}
					checked={isChecked}
					onChange={onChange}
					name={id as string}
					selectedIconName={ICONS.SQUARE_CHECKBOX_SELECTED}
					unselectedIconName={ICONS.SQUARE_CHECKBOX_UNSELECTED}
				/>
			)}
		</div>
	);
}
